// Vue Router configuration with lazy-loaded routes
import { createRouter, createWebHistory } from 'vue-router'
import store from './store.js'
import PageSync from './helpers/page-sync.js'
import VersionSync from './helpers/version-sync.js'

// Rails expects array params as key[]=value. Preserve that on both sides so
// round-tripping {state: ['in_progress']} survives browser navigation.
function parseQuery(search) {
  const query = {}
  for (const [key, value] of new URLSearchParams(search)) {
    if (key.endsWith('[]')) {
      const arrayKey = key.slice(0, -2)
      query[arrayKey] ||= []
      query[arrayKey].push(value)
    } else {
      query[key] = value
    }
  }
  return query
}

function stringifyQuery(query) {
  const params = []
  for (const key of Object.keys(query)) {
    const value = query[key]
    if (value === null || value === undefined) continue
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v === null || v === undefined) continue
        params.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`)
      }
    } else {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    }
  }
  return params.join('&')
}

const routes = [
  // Auth routes (guest only) - using Devise paths for backwards compatibility
  { path: '/account/sign_in', name: 'login', component: () => import('./pages/login.vue'), meta: { guest: true } },
  { path: '/account/password/new', name: 'forgot-password', component: () => import('./pages/forgot_password.vue'), meta: { guest: true } },
  { path: '/account/password/edit', name: 'reset-password', component: () => import('./pages/reset_password.vue'), meta: { guest: true } },
  { path: '/account/confirmation/new', name: 'request-confirmation', component: () => import('./pages/request_confirmation.vue'), meta: { guest: true } },
  { path: '/account/confirmation', name: 'confirm-account', component: () => import('./pages/confirm_account.vue'), meta: { guest: true } },

  // Home - redirects based on permissions
  { path: '/', name: 'home', component: () => import('./pages/home.vue') },

  // Projects
  { path: '/projects', name: 'projects', component: () => import('./pages/projects.vue') },
  { path: '/projects/new', name: 'project-new', component: () => import('./pages/project.vue'), props: (route) => ({ id: null, partnerId: route.query.partner_id ? Number(route.query.partner_id) : null }) },
  { path: '/projects/:id', name: 'project-show', component: () => import('./pages/project.vue'), props: true },

  // Project nested - phases/line_items
  { path: '/projects/:projectId/phases/:phaseId/line_items', name: 'line-items', component: () => import('./pages/line_items.vue'), props: true },

  // Project nested - tasks
  { path: '/projects/:projectId/tasks', name: 'project-tasks', component: () => import('./pages/tasks.vue'), props: true },
  { path: '/projects/:projectId/tasks/new', name: 'project-task-new', component: () => import('./pages/task.vue'), props: (route) => ({ id: null, projectId: route.params.projectId }) },
  { path: '/projects/:projectId/tasks/:id', name: 'project-task-show', component: () => import('./pages/task.vue'), props: true },

  // Project nested - folders
  { path: '/projects/:projectId/folders', name: 'project-folders', component: () => import('./pages/folders.vue'), props: true },

  // Project nested - messages (various types)
  { path: '/projects/:projectId/admin_internal_messages', name: 'project-admin-messages', component: () => import('./pages/messages.vue'), props: (route) => ({ projectId: route.params.projectId, messageType: 'admin_internal_messages' }) },
  { path: '/projects/:projectId/customer_messages', name: 'project-customer-messages', component: () => import('./pages/messages.vue'), props: (route) => ({ projectId: route.params.projectId, messageType: 'customer_messages' }) },
  { path: '/projects/:projectId/assembler_messages', name: 'project-assembler-messages', component: () => import('./pages/messages.vue'), props: (route) => ({ projectId: route.params.projectId, messageType: 'assembler_messages' }) },
  { path: '/projects/:projectId/quote_messages', name: 'project-quote-messages', component: () => import('./pages/messages.vue'), props: (route) => ({ projectId: route.params.projectId, messageType: 'quote_messages' }) },
  { path: '/projects/:projectId/assembly_order_messages', name: 'project-assembly-order-messages', component: () => import('./pages/messages.vue'), props: (route) => ({ projectId: route.params.projectId, messageType: 'assembly_order_messages' }) },
  { path: '/projects/:projectId/legacy_messages', name: 'project-legacy-messages', component: () => import('./pages/messages.vue'), props: (route) => ({ projectId: route.params.projectId, messageType: 'legacy_messages' }) },
  { path: '/projects/:projectId/messages', name: 'project-messages', component: () => import('./pages/messages.vue'), props: true },

  // Users
  { path: '/users', name: 'users', component: () => import('./pages/users.vue') },
  { path: '/users/new', name: 'user-new', component: () => import('./pages/user_form.vue'), props: { mode: 'new' } },
  { path: '/users/:id', name: 'user-edit', component: () => import('./pages/user_form.vue'), props: (route) => ({ userId: Number(route.params.id) }) },

  // Partners
  { path: '/partners', name: 'partners', component: () => import('./pages/partners.vue') },
  { path: '/partners/:id', name: 'partner-edit', component: () => import('./pages/partner_form.vue'), props: (route) => ({ partnerId: Number(route.params.id) }) },
  { path: '/partners/:partnerId/users/new', name: 'partner-user-new', component: () => import('./pages/user_form.vue'), props: (route) => ({ partnerId: Number(route.params.partnerId) }) },

  // Products
  { path: '/products', name: 'products', component: () => import('./pages/products.vue') },
  { path: '/products/:id', name: 'product-show', component: () => import('./pages/product.vue'), props: true },
  { path: '/products/:productId/folders', name: 'product-folders', component: () => import('./pages/folders.vue'), props: true },

  // Tasks (standalone)
  { path: '/tasks', name: 'tasks', component: () => import('./pages/tasks.vue') },
  { path: '/tasks/new', name: 'task-new', component: () => import('./pages/task.vue'), props: { id: null } },
  { path: '/tasks/:id', name: 'task-show', component: () => import('./pages/task.vue'), props: true },
  { path: '/tasks/:taskId/folders', name: 'task-folders', component: () => import('./pages/folders.vue'), props: true },

  // Attachment browser (admin only)
  { path: '/attachment_browser', name: 'attachment-browser', component: () => import('./pages/attachment_browser.vue') },

  // Static pages
  { path: '/links', name: 'links', component: () => import('./pages/links.vue') },

  // 404 catch-all (Vue 3 syntax)
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./pages/not_found.vue') }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  parseQuery,
  stringifyQuery,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { left: 0, top: 0 }
  }
})

// Progress bar for route transitions
router.beforeEach((to, from, next) => {
  store.dispatch('progress/increment')
  next()
})

// Version check - hard reload if app version changed
router.beforeEach((to, from, next) => {
  if (VersionSync.shouldHardReload()) {
    VersionSync.log('Hard reload to:', to.fullPath)
    document.location.href = to.fullPath
    return  // Don't call next()
  }
  next()
})

router.afterEach(() => {
  store.dispatch('progress/decrement')
})

// Auth guard - handle authentication redirects
router.beforeEach(async (to, from, next) => {
  // Fetch session if not initialized yet
  if (!store.state.app.initialized) {
    await store.dispatch('app/fetchSession')
  }

  const user = store.state.app.user
  const isGuestRoute = to.matched.some((record) => record.meta.guest)

  if (user && isGuestRoute) {
    // Logged in user trying to access guest-only page (login, etc)
    // Decrement progress since redirects abort navigation and afterEach won't fire
    store.dispatch('progress/decrement')
    next('/')
  } else if (!user && !isGuestRoute) {
    // Not logged in trying to access protected page
    // Decrement progress since redirects abort navigation and afterEach won't fire
    store.dispatch('progress/decrement')
    next({ path: '/account/sign_in', query: { redirect_to: to.fullPath } })
  } else {
    next()
  }
})

// Reset page-level stores on path change to prevent stale data
const globalModules = new Set(['app', 'flash', 'progress', 'confirm', 'project'])

router.beforeEach((to, from, next) => {
  if (to.path !== from.path) {
    for (const key in store.state) {
      if (!globalModules.has(key) && store.hasModule(key) && store._mutations[`${key}/resetState`]) {
        store.commit(`${key}/resetState`)
      }
    }
  }
  next()
})

// PageSync cleanup/subscribe on route change
router.afterEach((to) => {
  // Cleanup old subscriptions
  PageSync.cleanup()
  // Subscribe to new path
  PageSync.subscribe(to.path)
})

export default router
